import { RouteRecordRaw } from 'vue-router'
import { Component, markRaw } from 'vue'
import dayjs from 'dayjs'
import { pageDefaultPageNumber, pageDefaultPageSize, pageDefaultTotal } from '@/common/constants/constants'
import SparkMD5 from 'spark-md5'
import { TreeDepartmentResponse } from '@/api/system/department'
import { Dictionary, useDictionaryStore } from '@/stores/dictionary'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { CurrentDepartmentResponse } from '@/api/system/employee.ts'
import { Menu } from '@/stores/user.ts'
import { UploadRawFile } from 'element-plus'
import { InfoFilled } from '@element-plus/icons-vue'
import { Setting, useSettingStore } from '@/stores/setting.ts'

export const amountToYuan  = (amountFen?: number) => {
    if (!amountFen) return undefined
    if (amountFen === 0) {
        return 0
    }
    return amountFen / 100
}

export const amountToFen  = (amountYuan?: number) => {
    if (!amountYuan) return undefined
    if (amountYuan === 0) {
        return 0
    }
    return amountYuan * 100
}

export const getFilePrettySize = (filesize: number) => {
    if (!filesize) {
        return 'Unkown'
    }
    const unitArr = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    let index = 0
    const srcsize = parseFloat(String(filesize))
    index = Math.floor(Math.log(srcsize) / Math.log(1024))
    const size = srcsize / Math.pow(1024, index)
    const sizeString = size.toFixed(2) // 保留两位小数
    return sizeString + ' ' + unitArr[index]
}

export const getTimePeriod = () => {
    // 获取当前时间
    const now = new Date()
    // 获取小时数（24小时制）
    const hour = now.getHours()
    // 定义时间段
    const periods = {
        morning: [0, 11],
        afternoon: [12, 24],
    }
    // 遍历时间段，找到匹配的小时范围
    for (const period in periods) {
        if (period === 'morning') {
            if (hour >= periods.morning[0] && hour <= periods.morning[1]) {
                return period
            }
        } else if (period === 'afternoon') {
            if (hour >= periods.afternoon[0] && hour <= periods.afternoon[1]) {
                return period
            }
        }
        return ''
    }
}

export const getTimestamp = () => {
    return Date.now()
}

export const getCurrentYear = () => {
    return getDayjs().format('YYYY')
}

export const getSentryEnvironment = () => {
    if (import.meta.env.VITE_ENVIRONMENT === 'development') {
        return 'dev'
    } else if (import.meta.env.VITE_ENVIRONMENT === 'testing') {
        return 'test'
    } else if (import.meta.env.VITE_ENVIRONMENT === 'production') {
        return 'prod'
    } else {
        return ''
    }
}

export const getStorageSize = (storage: Storage) => {
    let totalSize = 0
    for (const key in storage) {
        if (storage.hasOwnProperty(key)) {
            const value = storage[key]
            // 使用 UTF-8 编码计算字符串长度（每个字符可能占用1到4个字节）
            // 这里简化处理，假设每个字符占用2个字节（对于ASCII字符是足够的，但可能低估了非ASCII字符的大小）
            const size = new Blob([value], { type: 'text/plain;charset=utf-8' }).size
            totalSize += size
        }
    }
    return totalSize
}

/**
 * 简易深度克隆
 * @param target
 */
export function easyDeepClone<T = any>(target: T): T {
    return JSON.parse(JSON.stringify(target))
}

export const toRoutePath = (path: string) => {
    if (!path) {
        return ''
    }
    if (path === '/') {
        return import.meta.env.VITE_SERVER_CONTEXT || '/'
    }
    if (path.startsWith('http')) {
        return path
    }
    if (!path.startsWith('/')) {
        path = '/' + path
    }
    return (import.meta.env.VITE_SERVER_CONTEXT || '') + path
}

export const routePathToName = (path: string) => {
    if (!path) {
        return ''
    }
    if (path.startsWith('/')) {
        path = path.replace('/', '')
    }
    return path.replace(/\//g, '_')
}

export const isHttp = (url?: string) => {
    return !!url?.startsWith('http')
}

export const hasHttpQuery = (url?: string) => {
    return url?.indexOf('?') !== -1
}

export const parseHttp = (url?: string) => {
    return url?.split('?') || []
}

export const concatHttp = (prefixUrl?: string, suffixQuery?: string) => {
    if (prefixUrl && suffixQuery) {
        return prefixUrl + '?' + suffixQuery
    }
    return prefixUrl || ''
}

export const getRouteByName = (routeList: RouteRecordRaw[], routeName: string) => {
    for (const route of routeList) {
        if (route.name === routeName) {
            return route
        }
        if (route.children) {
            const temp = getRouteByName(route.children, routeName)
            if (temp) {
                return temp
            }
        }
    }
}

export const getMenuByKey = (menList: Menu[], menuKey: string) => {
    // console.log(menList, menuKey)
    for (const menu of menList) {
        if (menu.key == menuKey) {
            return menu
        }
        if (menu.children) {
            const temp = getMenuByKey(menu.children, menuKey)
            if (temp) {
                return temp
            }
        }
    }
}

export type ConfirmModalOptions = {
    title?: string
    message?: string
    icon?: string | Component
    type?: '' | 'success' | 'warning' | 'info' | 'error'
    cancelButtonText?: string
    confirmButtonText?: string
}

export const confirmModal = (options: ConfirmModalOptions) => {
    const mergeOptions: ConfirmModalOptions = {
        ...{
            title: '提示',
            icon: markRaw(InfoFilled),
            type: 'info',
            message: '确定操作吗',
            confirmButtonText: '确定',
            cancelButtonText: '取消',
        },
        ...options,
    }
    return ElMessageBox.confirm(mergeOptions.message, {
        title: mergeOptions.title,
        type: mergeOptions.type,
        icon: mergeOptions.icon,
        confirmButtonText: mergeOptions.confirmButtonText,
        cancelButtonText: mergeOptions.cancelButtonText,
    })
}

export const deleteConfirmModal = (options?: ConfirmModalOptions) => {
    return confirmModal({
        message: '确定删除吗',
        ...options,
    })
}

export const formatTimestamp = (timestamp: any, format?: string) => {
    if (!timestamp) {
        return ''
    }
    return getDayjs(timestamp).format(format || 'YYYY-MM-DD HH:mm:ss')
}

export const getDayjs = (date?: dayjs.ConfigType, format?: dayjs.OptionType, locale?: string, strict?: boolean) => {
    dayjs.extend(utc)
    dayjs.extend(timezone)
    dayjs.tz.setDefault('Asia/Shanghai')
    // dayjs.extend(advancedFormat);
    return dayjs(date, format, locale, strict)
}

export const setAll = (obj, val) => Object.keys(obj).forEach(k => (obj[k] = val))

export const initPage = (options?: {}) => {
    return {
        pageNumber: pageDefaultPageNumber,
        pageSize: pageDefaultPageSize,
        totalSize: pageDefaultTotal,
        ...options,
    }
}

/**
 * 获取文件唯一标识（md5）
 * @param file
 */
export const getFileUnionCode = (file: UploadRawFile) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        fileReader.onload = () => {
            file.arrayBuffer()
                .then(arrayBuffer => {
                    const md5Hash = SparkMD5.ArrayBuffer.hash(arrayBuffer as any)
                    resolve(md5Hash)
                })
                .catch(error => {
                    reject(error)
                })
        }

        fileReader.onerror = error => {
            reject(error)
        }
    })
}

/**
 * 获取图片 base64
 * @param image
 * @param callback
 */
export const getImageBase64 = (image: Blob, callback: (base64Url: string) => void) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => callback(reader.result as string))
    reader.readAsDataURL(image)
}

export const reloadToIndex = () => {
    window.location.href = toRoutePath('/')
}

export const getDepartmentNamePath = (
    tree?: CurrentDepartmentResponse | TreeDepartmentResponse,
    separator?: string,
) => {
    if (!tree) {
        return ''
    }
    const namePathArray = []
    forEachGetDepartmentNamePath(tree, namePathArray)
    if (!separator) {
        separator = ' / '
    }
    return namePathArray.join(separator)
}

const forEachGetDepartmentNamePath = (
    tree: CurrentDepartmentResponse | TreeDepartmentResponse,
    namePathArray: string[],
) => {
    if (!tree) {
        return
    }
    if (tree.name) {
        namePathArray.push(tree.name)
    }
    if (tree.hasOwnProperty('tree')) {
        tree['tree']?.forEach(item => {
            forEachGetDepartmentNamePath(item, namePathArray)
        })
    }
    if (tree.hasOwnProperty('children')) {
        tree['children']?.forEach(item => {
            forEachGetDepartmentNamePath(item, namePathArray)
        })
    }
}

export const getDictionaries = (categoryCodeList: string[], orKeyList?: string[]) => {
    const dictionaryStore = useDictionaryStore()
    return dictionaryStore.pull(categoryCodeList, orKeyList)
}

/**
 * 获取字典
 * @param categoryCode
 * @param dictionaries
 */
export const getDictionaryByCategoryCode = (categoryCode: string | string[]) => {
    const dictionaryStore = useDictionaryStore()
    const isArray = Array.isArray(categoryCode)
    if (isArray) {
        const arr: any[] = []
        for (let index = 0; index < categoryCode.length; index++) {
            const tempArr = dictionaryStore.map[categoryCode[index]]?.dictionaries
                ?.filter(item => item.key && item.key !== '')
                .sort((a, b) => (a.sort && b.sort ? b.sort - a.sort : 0)) as any[]
            if (tempArr) {
                arr.push(...tempArr)
            }
        }
        return arr
    } else {
        return dictionaryStore.map[categoryCode]?.dictionaries
            ?.filter(item => item.key && item.key !== '')
            .sort((a, b) => (a.sort && b.sort ? b.sort - a.sort : 0))
    }
}

export const getDictionaryNameByCategoryCode = (value: any, categoryCode: string | string[]) => {
    return getDictionaryByCategoryCode(categoryCode)?.find(item => item?.value === value)?.label || value
}

export const getDictionaryValuesByCategoryCode = (categoryCode: string | string[]) => {
    return getDictionaryByCategoryCode(categoryCode)?.map(item => item?.value)
}

export const getDictionaryValueByCategoryCode = (categoryCode: string | string[]) => {
    const values = getDictionaryValuesByCategoryCode(categoryCode)
    if (values && values.length > 0) {
        return values[0]
    }
}

export const getDictionaryByKey = (key: string | string[]): Dictionary[] => {
    const dictionaryStore = useDictionaryStore()
    const isArray = Array.isArray(key)
    if (isArray) {
        const arr: Dictionary[] = []
        for (let index = 0; index < key.length; index++) {
            const dict = dictionaryStore.keyMap[key[index]]
            if (dict) {
                arr.push(dict)
            }
        }
        return arr
    } else {
        const dict = dictionaryStore.keyMap[key]
        if (dict) {
            return [dict]
        }
        return []
    }
}

export const getDictionaryValueByKey = (key: string | string[]) => {
    const dicts = getDictionaryByKey(key)
    if (dicts && dicts.length > 0) {
        return dicts[0].value
    }
}

export const getSettings = (code: string | string[]) => {
    const settingStore = useSettingStore()
    const isArray = Array.isArray(code)
    if (isArray) {
        return settingStore.pull(code)
    } else {
        return settingStore.pull([code])
    }
}

export const getSettingByCode = (code: string | string[]): Setting | Setting[] => {
    const settingStore = useSettingStore()
    const isArray = Array.isArray(code)
    if (isArray) {
        const arr: any[] = []
        for (let index = 0; index < code.length; index++) {
            const temp = settingStore.map[code[index]]?.setting
            arr.push(temp)
        }
        return arr
    } else {
        return settingStore.map[code]?.setting
    }
}

export const getSettingNameByCode = (value: any, code: string | string[]) => {
    const array = getSettingByCode(code)
    const isArray = Array.isArray(array)
    if (isArray) {
        return array?.find(item => item?.value === value)?.name || value
    } else {
        if (array?.value === value) {
            return array?.name
        }
    }
    return value
}

export const getSettingValuesByCode = (code: string | string[]) => {
    const array = getSettingByCode(code)
    // console.log(array)
    const isArray = Array.isArray(code)
    if (isArray) {
        return (array as Setting[])?.map(item => item?.value)
    } else {
        return [(array as Setting)?.value]
    }
}

export const mapSettingValuesByCode = (code: string | string[]): Record<string, string> => {
    const array = getSettingByCode(code)
    // console.log(array)
    const isArray = Array.isArray(code)
    const map: Record<string, string> = {}
    if (isArray) {
        ;(array as Setting[])?.forEach(item => {
            if (item?.code) {
                map[item.code] = item.value
            }
        })
    } else {
        const item = (array as Setting)?.value
        if (item?.code) {
            map[item.code] = item.value
        }
    }
    return map
}

export const getSettingValueByCode = (code: string | string[]) => {
    const array = getSettingValuesByCode(code)
    if (array) {
        // console.log(array)
        return array[0]
    }
}

export const isNumber = (value: any) => {
    return /^-?\d+$/.test(value)
}

export const isBoolean = (value: any) => {
    return value === true || value === false || toString.call(value) === '[object Boolean]'
}

// export const getImageRender = (downloadUrl: string, width?: number, height?: number) => {
//     const defaultWidth = 60
//     const defaultHeight = 60
//     if (!width) {
//         width = defaultWidth
//     }
//     if (!height) {
//         height = defaultHeight
//     }
//     if (downloadUrl) {
//         return h(
//             Image,
//             {
//                 src: downloadUrl,
//                 width: width,
//                 height: height,
//                 fallback: imageFallback,
//             },
//             {
//                 previewMask: () => h(EyeOutlined),
//             },
//         )
//     } else {
//         return h(
//             Image,
//             {
//                 src: imageFallback,
//                 width: width,
//                 height: height,
//             },
//             {
//                 previewMask: () => h(EyeOutlined),
//             },
//         )
//     }
// }

// export const getMimeTypeCustomerRender = (mimeType: string, downloadUrl: string, isFolder: number) => {
//     mimeType = mimeType || ''
//     const fontSize = 60
//     if (isFolder === YesOrNoEnum.YES) {
//         return h(FolderOutlined, { style: { fontSize: fontSize + 'px' } })
//     } else if (mimeType.startsWith('image/')) {
//         return getImageRender(downloadUrl)
//     } else if (mimeType.indexOf('application/wps-office.xlsx') > -1) {
//         return h(FileExcelOutlined, { style: { fontSize: fontSize + 'px' } })
//     } else if (mimeType.indexOf('application/wps-office.docx') > -1) {
//         return h(FileWordOutlined, { style: { fontSize: fontSize + 'px' } })
//     } else if (mimeType.indexOf('application/wps-office.pptx') > -1) {
//         return h(FilePptOutlined, { style: { fontSize: fontSize + 'px' } })
//     } else {
//         return h(FileUnknownOutlined, { style: { fontSize: fontSize + 'px' } })
//     }
// }

export const imageFallback =
    'data:image/png;base64,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'
